import classNames from 'classnames'
import { useEffect, useRef } from 'react'
import { AlertIcon, InfoIcon } from '~/theme/icons'
import { focusRing } from '~/utils/styles'

export interface AlertProps {
  title?: string
  message?: string
  children?: React.ReactNode
  showIcon?: boolean
  className?: string
  autoFocus?: boolean
  level?: 'info' | 'warning' | 'success'
}

export const Alert = ({
  title,
  message,
  showIcon = true,
  className,
  level = 'warning',
  // warning: autoFocus should NOT be used if the <Alert> is visible when the page loads.
  // https://www.boia.org/blog/accessibility-tips-be-cautious-when-using-autofocus
  // It should only be used to bring alerts into view after a user action, like a form submission.
  autoFocus = false,
  children,
}: AlertProps): React.ReactElement => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref.current && autoFocus) {
      // scroll smoothly to ref
      ref.current.focus({ preventScroll: true })
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      })
    }
  }, [
    autoFocus,
    // scroll to ref when data changes (this might be the same code/message, but a new submission)
    title,
    message,
  ])

  const IconComponent = level === 'info' ? InfoIcon : AlertIcon

  return (
    <section
      className={classNames(
        'shadow-1-l scroll-m-10 rounded-lg border-2 px-4 py-3 ring-4',
        level === 'info' && 'border-blue-600 bg-blue-50 ring-blue-50',
        level === 'warning' && 'border-yellow-500 bg-yellow-50 ring-yellow-50',
        level === 'success' && 'border-green-700 bg-green-100 ring-green-100',
        focusRing,
        className,
      )}
      ref={ref}
      tabIndex={-1}
    >
      <div className='flex items-start gap-3'>
        {showIcon && (
          <div className={'pt-1'}>
            <IconComponent
              className={classNames(
                'w-6 shrink-0 self-center',
                level === 'info' && 'text-blue-600',
                level === 'warning' && 'text-yellow-500',
                level === 'success' && 'text-green-700',
              )}
              aria-hidden
            />
          </div>
        )}
        <div className='flex flex-col justify-center gap-2'>
          {title && (
            <h3
              className={classNames(
                'text-h4 font-medium',
                level === 'info' && 'text-blue-700',
                level === 'warning' && 'text-yellow-600',
                level === 'success' && 'text-green-700',
              )}
            >
              {title}
            </h3>
          )}
          {message && (
            <div
              className={classNames(
                'text-body2',
                level === 'info' && 'text-blue-700',
                level === 'warning' && 'text-yellow-900',
              )}
            >
              {message}
            </div>
          )}

          {children}
        </div>
      </div>
    </section>
  )
}
